import { useState } from "react";

// ── Stripe & Sheets ──
const STRIPE_BANQUET_LINK = "https://buy.stripe.com/6oU6oHab7g7x56Z6DJbQY14";
const TICKET_PRICE = 10;

// Deployed Apps Script Web App URLs (one per sheet)
const SHEETS_URLS = {
  seniorQuestionnaire: "https://script.google.com/macros/s/AKfycby3L6C5zd0mWDh5JiuI5lA5k0LkDUv0iQ-zuUoLwZcgw_tU-5_E4NRmxnAIJUpIMLxX1w/exec",
  banquetRSVP: "https://script.google.com/macros/s/AKfycbx-O47XfPfWJdR7uHMq3dCF3naX-C6ie1IjStObkovn80HS9MNf_cc6kwIQU6hfMDHU2w/exec",
};

// ── Helpers ──
function TextField({ label, value, onChange, type = "text", placeholder, required, hint }) {
  return (
    <div style={styles.fieldGroup}>
      <label style={styles.label}>{label} {required && <span style={styles.req}>*</span>}</label>
      <input
        style={styles.input}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
      />
      {hint && <div style={styles.hint}>{hint}</div>}
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder, rows = 3, required, hint }) {
  return (
    <div style={styles.fieldGroup}>
      <label style={styles.label}>{label} {required && <span style={styles.req}>*</span>}</label>
      <textarea
        style={{ ...styles.input, resize: "vertical", minHeight: rows * 22 + 20, fontFamily: font }}
        rows={rows}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
      />
      {hint && <div style={styles.hint}>{hint}</div>}
    </div>
  );
}

function StripeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 32 32" fill="none" style={{ marginRight: 8 }}>
      <rect width="32" height="32" rx="6" fill="#635BFF" />
      <path d="M15.2 13.2c0-.8.7-1.1 1.8-1.1 1.6 0 3.6.5 5.2 1.4V9.2c-1.7-.7-3.5-1-5.2-1-4.3 0-7.1 2.2-7.1 6 0 5.8 8 4.9 8 7.4 0 .9-.8 1.2-1.9 1.2-1.7 0-3.8-.7-5.5-1.6v4.4c1.9.8 3.8 1.2 5.5 1.2 4.4 0 7.3-2.1 7.3-5.9-.1-6.3-8.1-5.2-8.1-7.7z" fill="#fff" />
    </svg>
  );
}

// ── Page: Home ──
function HomePage({ onNavigate }) {
  return (
    <>
      <div style={{ ...styles.heroSection, backgroundImage: "linear-gradient(180deg, rgba(5,10,20,0.45) 0%, rgba(13,37,82,0.85) 100%), url(/pics/hero.jpg)" }}>
        <div style={styles.heroOverlay}>
          <div style={styles.heroEyebrow}>SPRING 2026</div>
          <div style={styles.heroTagline}>Two Events. One Week.</div>
          <div style={styles.heroLead}>
            Honor the seniors. Close out the year. Celebrate together.
          </div>
        </div>
      </div>

      <div style={styles.eventCard} onClick={() => onNavigate("senior")} role="button" tabIndex={0}>
        <div style={{ ...styles.eventCardImage, backgroundImage: "url(/pics/seniornight.jpg)" }} />
        <div style={styles.eventCardBody}>
          <div style={styles.eventCardEdition}>6TH ANNUAL</div>
          <div style={styles.eventCardTitle}>Senior Night Game</div>
          <div style={styles.eventCardMeta}>
            Thu · May 7, 2026 · USF Baseball Stadium
          </div>
          <div style={styles.eventCardSummary}>
            Honor our seniors with a game at USF. Free admission — bring family and friends.
          </div>
          <div style={styles.eventCardCTA}>Senior Questionnaire & Details →</div>
        </div>
      </div>

      <div style={styles.eventCard} onClick={() => onNavigate("banquet")} role="button" tabIndex={0}>
        <div style={{ ...styles.eventCardImage, backgroundImage: "url(/pics/banquet.jpg)" }} />
        <div style={styles.eventCardBody}>
          <div style={styles.eventCardEdition}>6TH ANNUAL</div>
          <div style={styles.eventCardTitle}>Awards Banquet</div>
          <div style={styles.eventCardMeta}>
            Fri · May 8, 2026 · Grace Family Church · Lutz, FL
          </div>
          <div style={styles.eventCardSummary}>
            Closing the year — awards, college commitments, dinner. Players free, $10 per family member.
          </div>
          <div style={styles.eventCardCTA}>RSVP & Buy Tickets →</div>
        </div>
      </div>
    </>
  );
}

// ── Page: Senior Night ──
function SeniorNightPage({ onNavigate }) {
  const [form, setForm] = useState({
    playerName: "",
    walkingWith: "",
    collegeCommitment: "",
    favoriteMemory: "",
    finalWords: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));
  const isValid = form.playerName && form.walkingWith && form.favoriteMemory && form.finalWords;

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");
    const payload = {
      timestamp: new Date().toLocaleString(),
      playerName: form.playerName,
      walkingWith: form.walkingWith,
      collegeCommitment: form.collegeCommitment || "N/A",
      favoriteMemory: form.favoriteMemory,
      finalWords: form.finalWords,
    };
    try {
      await fetch(SHEETS_URLS.seniorQuestionnaire, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Sheet submit error:", err);
      setSubmitted(true);
      setSubmitError("Saved locally. Sheet sync may be delayed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button style={styles.backLink} onClick={() => onNavigate("home")}>← All Events</button>

      <div style={{ ...styles.heroSection, ...styles.heroSectionDetail, backgroundImage: "linear-gradient(180deg, rgba(5,10,20,0.4) 0%, rgba(13,37,82,0.85) 100%), url(/pics/USF_Baseball_Stadium.jpg)" }}>
        <div style={styles.heroOverlay}>
          <div style={styles.heroEyebrow}>6TH ANNUAL · A3 ACADEMY</div>
          <div style={styles.heroTagline}>Senior Night Game</div>
          <div style={styles.heroLead}>USF Baseball Stadium · Thursday, May 7, 2026</div>
        </div>
      </div>

      <div style={styles.sectionLabel}>Schedule</div>
      <div style={styles.timeStrip}>
        <div style={styles.timeRow}>
          <span style={styles.timeLabel}>Gates Open</span>
          <span style={styles.timeValue}>5:45 PM</span>
        </div>
        <div style={styles.timeRow}>
          <span style={styles.timeLabel}>Ceremony</span>
          <span style={styles.timeValue}>6:00 PM</span>
        </div>
        <div style={styles.timeRow}>
          <span style={styles.timeLabel}>First Pitch</span>
          <span style={styles.timeValue}>6:35 PM</span>
        </div>
      </div>

      <div style={styles.calloutBox}>
        <div style={styles.calloutLabel}>Admission</div>
        <div style={styles.calloutValue}>FREE</div>
        <div style={styles.calloutText}>
          Bring as many friends and family as you'd like — let's fill up the park for our seniors.
        </div>
      </div>

      <div style={styles.sectionLabel}>For Senior Families</div>
      <div style={styles.infoBox}>
        Parents and families walking with their seniors — please be early/on time. The ceremony starts <strong>promptly at 6:00 PM</strong> with families walking onto the field.
      </div>

      <div style={styles.sectionLabel}>How the Game Works</div>
      <div style={styles.infoBox}>
        Seniors drafted the teams and coach the teams. Per rules, all seniors must play a minimum number of innings. Juniors, sophomores, and freshmen are eligible to be drafted onto either team.
      </div>

      {!submitted ? (
        <>
          <div style={styles.sectionLabel}>Senior Questionnaire</div>
          <div style={styles.formCard}>
            <p style={styles.formIntro}>
              Fill this out so we can recognize you properly during the ceremony — your story, your family, your future.
            </p>
            <TextField
              label="Player Name"
              value={form.playerName}
              onChange={set("playerName")}
              placeholder="Your full name"
              required
            />
            <TextArea
              label="Who is walking onto the field with you?"
              value={form.walkingWith}
              onChange={set("walkingWith")}
              placeholder="List names and relationships, e.g., 'Mom — Sarah Smith, Dad — Mike Smith, Grandpa — Joe, Girlfriend — Jenny'"
              rows={4}
              required
              hint="Mom, dad, siblings, grandparents, aunts/uncles, girlfriend — whoever's walking out with you."
            />
            <TextField
              label="College Commitment (if any)"
              value={form.collegeCommitment}
              onChange={set("collegeCommitment")}
              placeholder="University / college name"
              hint="Leave blank if uncommitted."
            />
            <TextArea
              label="Favorite memory at A3"
              value={form.favoriteMemory}
              onChange={set("favoriteMemory")}
              placeholder="A moment, a teammate, a game, a coach..."
              rows={4}
              required
            />
            <TextArea
              label="Final words / advice / quote for A3 players"
              value={form.finalWords}
              onChange={set("finalWords")}
              placeholder="Something you'd want the younger guys to hear"
              rows={4}
              required
            />
            <button
              style={{ ...styles.primaryBtn, ...(isValid && !submitting ? {} : styles.btnDisabled) }}
              disabled={!isValid || submitting}
              onClick={handleSubmit}
            >
              {submitting ? "Submitting..." : "Submit Questionnaire"}
            </button>
          </div>
        </>
      ) : (
        <div style={styles.successBox}>
          <div style={styles.successIcon}>✓</div>
          <div style={styles.successTitle}>Got it, {form.playerName.split(" ")[0]}!</div>
          {submitError && <p style={{ color: colors.accentSoft, fontSize: 13, marginBottom: 12 }}>{submitError}</p>}
          <p style={styles.successText}>
            We'll have your story ready for the ceremony on May 7. See you at USF.
          </p>
        </div>
      )}
    </>
  );
}

// ── Page: Banquet ──
function BanquetPage({ onNavigate }) {
  const [form, setForm] = useState({
    playerName: "",
    ticketCount: "0",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));
  const ticketCountNum = parseInt(form.ticketCount, 10) || 0;
  const subtotal = ticketCountNum * TICKET_PRICE;
  const isValid = form.playerName.length > 1;

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");
    const payload = {
      timestamp: new Date().toLocaleString(),
      playerName: form.playerName,
      ticketCount: ticketCountNum,
      subtotal: `$${subtotal}`,
      paymentStatus: ticketCountNum > 0 ? "Pending Stripe" : "N/A (player only)",
    };
    try {
      await fetch(SHEETS_URLS.banquetRSVP, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Sheet submit error:", err);
      setSubmitted(true);
      setSubmitError("Saved locally. Sheet sync may be delayed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button style={styles.backLink} onClick={() => onNavigate("home")}>← All Events</button>

      <div style={{ ...styles.heroSection, ...styles.heroSectionDetail, backgroundImage: "linear-gradient(180deg, rgba(5,10,20,0.45) 0%, rgba(13,37,82,0.88) 100%), url(/pics/banquet.jpg)" }}>
        <div style={styles.heroOverlay}>
          <div style={styles.heroEyebrow}>6TH ANNUAL · A3 ACADEMY</div>
          <div style={styles.heroTagline}>2026 Awards Banquet</div>
          <div style={styles.heroLead}>Friday, May 8, 2026 · 6:00 – 8:30 PM</div>
        </div>
      </div>

      <div style={styles.sectionLabel}>Where</div>
      <div style={styles.locationCard}>
        <div style={styles.locationCampus}>Grace Family Church</div>
        <div style={styles.locationAddress}>22920 SR 54 East · Lutz, FL</div>
      </div>

      <div style={styles.sectionLabel}>Agenda</div>
      <div style={styles.timeStrip}>
        <div style={styles.timeRow}>
          <span style={styles.timeLabel}>6:00 – 6:45 PM</span>
          <span style={styles.timeValue}>Eat &amp; Mingle</span>
        </div>
        <div style={styles.timeRow}>
          <span style={styles.timeLabel}>6:45 – 8:00 PM</span>
          <span style={styles.timeValue}>Awards Ceremony</span>
        </div>
        <div style={styles.timeRow}>
          <span style={styles.timeLabel}>8:00 – 8:30 PM</span>
          <span style={styles.timeValue}>College Commitments</span>
        </div>
      </div>

      <div style={styles.sectionLabel}>What to Wear</div>
      <div style={styles.infoBox}>
        <strong>Casual.</strong> Players should wear shorts, jeans, or khakis with a collared shirt. <em>No tank tops, no slides, no Crocs — make sure shirts have sleeves.</em>
      </div>

      <div style={styles.sectionLabel}>Food</div>
      <div style={styles.infoBox}>
        Catered by <strong>Caribbean Twist</strong> — Cuban sandwiches, chicken and rice, and more. Enough food for 200 people, one medium plate each.
      </div>

      <div style={styles.sectionLabel}>Tickets</div>
      <div style={styles.calloutBox}>
        <div style={styles.calloutLabel}>Players</div>
        <div style={styles.calloutValue}>FREE</div>
        <div style={styles.calloutText}>
          Family/friend tickets are <strong>${TICKET_PRICE} each</strong>. Capacity is 200. RSVP below, then check out via Stripe.
        </div>
      </div>

      <div style={styles.sectionLabel}>About the Ceremony</div>
      <div style={styles.infoBox}>
        All A3 players are welcome. We'll close out the year, give out awards across all ages, and honor players who committed to college. <strong>Players who already signed during the November signing period won't sign again</strong> — this ceremony is for players who committed between November and now. Bring your college shirt and a fake LOI; we'll take pictures and announce your commitment.
      </div>

      {!submitted ? (
        <>
          <div style={styles.sectionLabel}>RSVP</div>
          <div style={styles.formCard}>
            <p style={styles.formIntro}>
              Tell us how many family/friend tickets you need. Players don't need a ticket.
            </p>
            <TextField
              label="Player Name"
              value={form.playerName}
              onChange={set("playerName")}
              placeholder="Your full name"
              required
            />
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Family / Friend Tickets <span style={styles.req}>*</span></label>
              <select
                style={styles.input}
                value={form.ticketCount}
                onChange={e => set("ticketCount")(e.target.value)}
              >
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                  <option key={n} value={n}>
                    {n === 0 ? "0 tickets — just the player is coming" : `${n} ticket${n === 1 ? "" : "s"} — $${n * TICKET_PRICE}`}
                  </option>
                ))}
              </select>
              <div style={styles.hint}>Players are free — count only family and friends.</div>
            </div>

            {ticketCountNum > 0 && (
              <div style={styles.subtotalBox}>
                <span style={styles.subtotalLabel}>Subtotal</span>
                <span style={styles.subtotalValue}>${subtotal}.00</span>
              </div>
            )}

            <button
              style={{ ...styles.primaryBtn, ...(isValid && !submitting ? {} : styles.btnDisabled) }}
              disabled={!isValid || submitting}
              onClick={handleSubmit}
            >
              {submitting ? "Saving RSVP..." : "Submit RSVP"}
            </button>
          </div>
        </>
      ) : (
        <div style={styles.successBox}>
          <div style={styles.successIcon}>✓</div>
          <div style={styles.successTitle}>RSVP Saved</div>
          {submitError && <p style={{ color: colors.accentSoft, fontSize: 13, marginBottom: 12 }}>{submitError}</p>}
          <p style={styles.successText}>
            {ticketCountNum > 0 ? (
              <>
                Click below to buy your <strong>{ticketCountNum} ticket{ticketCountNum > 1 ? "s" : ""} (${subtotal})</strong> via Stripe.
                You can adjust the quantity at checkout if needed.
              </>
            ) : (
              <>You're all set, {form.playerName.split(" ")[0]} — see you on May 8 at Grace Family Church.</>
            )}
          </p>
          {ticketCountNum > 0 && (
            <a href={STRIPE_BANQUET_LINK} target="_blank" rel="noopener noreferrer" style={styles.stripeBtn}>
              <StripeIcon />
              Buy {ticketCountNum} Ticket{ticketCountNum > 1 ? "s" : ""} (${subtotal}) on Stripe →
            </a>
          )}
        </div>
      )}
    </>
  );
}

// ── App ──
export default function App() {
  const [page, setPage] = useState("home");

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.logoRow} onClick={() => setPage("home")}>
            <img src="/pics/a3-logo.png" alt="A3 Academy" style={styles.logoMark} />
            <div style={styles.brandText}>
              <div style={styles.brandSub}>A3 ACADEMY</div>
              <div style={styles.brandYear}>EVENTS</div>
            </div>
          </div>
          <div style={styles.headerTagline}>Spring 2026</div>
        </div>

        {page === "home" && <HomePage onNavigate={setPage} />}
        {page === "senior" && <SeniorNightPage onNavigate={setPage} />}
        {page === "banquet" && <BanquetPage onNavigate={setPage} />}

        <div style={styles.footer}>
          A3 Academy · Tampa & Jacksonville, FL · Spring 2026
        </div>
      </div>
    </div>
  );
}

// ── STYLES ──
const colors = {
  bg: "#050a14",
  card: "#0d1929",
  cardBorder: "#1a2a47",
  accent: "#2d70d0",
  accentDeep: "#0d2552",
  accentSoft: "#a8c8e8",
  accentGlow: "rgba(45, 112, 208, 0.18)",
  text: "#e8edf5",
  textMuted: "#8a96aa",
  input: "#0f1d33",
  inputBorder: "#2a4170",
  stripe: "#635BFF",
  success: "#2ecc71",
};

const font = "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif";

const styles = {
  page: {
    minHeight: "100vh",
    background: colors.bg,
    fontFamily: font,
    color: colors.text,
    padding: "24px 16px",
  },
  container: {
    maxWidth: 660,
    margin: "0 auto",
  },

  // Header
  header: {
    textAlign: "center",
    marginBottom: 32,
    paddingBottom: 24,
    borderBottom: `1px solid ${colors.cardBorder}`,
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    marginBottom: 8,
    cursor: "pointer",
  },
  logoMark: {
    width: 96,
    height: 96,
    objectFit: "contain",
    display: "block",
    clipPath: "circle(48% at center)",
  },
  brandText: {
    textAlign: "left",
    lineHeight: 1,
  },
  brandSub: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 4,
    color: colors.accentSoft,
    marginBottom: 4,
  },
  brandYear: {
    fontSize: 36,
    fontWeight: 900,
    letterSpacing: 2,
    color: colors.text,
    lineHeight: 1,
    textShadow: `0 0 24px ${colors.accentGlow}`,
  },
  headerTagline: {
    fontSize: 14,
    color: colors.textMuted,
    marginTop: 10,
  },

  // Hero
  heroSection: {
    position: "relative",
    minHeight: 320,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 14,
    marginBottom: 32,
    background: `linear-gradient(135deg, ${colors.card}, ${colors.accentDeep})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    overflow: "hidden",
    display: "flex",
    alignItems: "flex-end",
  },
  heroSectionDetail: {
    minHeight: 260,
  },
  heroOverlay: {
    width: "100%",
    padding: "32px 24px 28px",
    textAlign: "center",
  },
  heroEyebrow: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 3,
    color: colors.accentSoft,
    marginBottom: 10,
    textTransform: "uppercase",
  },
  heroTagline: {
    fontSize: 30,
    fontWeight: 900,
    letterSpacing: 1.5,
    color: colors.text,
    marginBottom: 12,
    lineHeight: 1.15,
    textShadow: "0 2px 16px rgba(0,0,0,0.6)",
  },
  heroLead: {
    fontSize: 14.5,
    fontWeight: 600,
    color: colors.text,
    lineHeight: 1.5,
    maxWidth: 460,
    margin: "0 auto",
    textShadow: "0 1px 8px rgba(0,0,0,0.7)",
  },

  // Event cards (home)
  eventCard: {
    background: colors.card,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 14,
    overflow: "hidden",
    marginBottom: 18,
    cursor: "pointer",
    transition: "border-color 0.2s, transform 0.2s",
  },
  eventCardImage: {
    width: "100%",
    height: 200,
    backgroundSize: "cover",
    backgroundPosition: "center",
    borderBottom: `2px solid ${colors.accent}`,
  },
  eventCardBody: {
    padding: "20px 22px",
  },
  eventCardEdition: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 2,
    color: colors.accentSoft,
    marginBottom: 8,
  },
  eventCardTitle: {
    fontSize: 24,
    fontWeight: 800,
    color: colors.text,
    marginBottom: 8,
    lineHeight: 1.15,
  },
  eventCardMeta: {
    fontSize: 13,
    color: colors.textMuted,
    fontWeight: 600,
    marginBottom: 12,
  },
  eventCardSummary: {
    fontSize: 14,
    color: colors.text,
    lineHeight: 1.55,
    marginBottom: 14,
  },
  eventCardCTA: {
    fontSize: 14,
    fontWeight: 700,
    color: colors.accent,
    letterSpacing: 0.3,
  },

  // Back link
  backLink: {
    background: "transparent",
    border: "none",
    color: colors.accentSoft,
    fontSize: 13,
    fontWeight: 700,
    fontFamily: font,
    cursor: "pointer",
    padding: "8px 0",
    marginBottom: 8,
    letterSpacing: 0.5,
  },

  // Section labels
  sectionLabel: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 2,
    textTransform: "uppercase",
    color: colors.accentSoft,
    marginBottom: 12,
    marginTop: 8,
  },

  // Time strip (used for schedule + agenda)
  timeStrip: {
    background: colors.card,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 10,
    padding: "8px 18px",
    marginBottom: 24,
  },
  timeRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 0",
    borderBottom: `1px solid ${colors.cardBorder}`,
  },
  timeLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: colors.textMuted,
    letterSpacing: 0.5,
  },
  timeValue: {
    fontSize: 15,
    fontWeight: 700,
    color: colors.text,
  },

  // Callout box
  calloutBox: {
    background: colors.accentGlow,
    border: `1px solid ${colors.accent}55`,
    borderRadius: 12,
    padding: "20px 22px",
    textAlign: "center",
    marginBottom: 24,
  },
  calloutLabel: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 2,
    color: colors.accentSoft,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  calloutValue: {
    fontSize: 32,
    fontWeight: 900,
    color: colors.accent,
    letterSpacing: 1,
    marginBottom: 8,
  },
  calloutText: {
    fontSize: 14,
    color: colors.text,
    lineHeight: 1.55,
    maxWidth: 440,
    margin: "0 auto",
  },

  // Info box
  infoBox: {
    background: colors.input,
    border: `1px solid ${colors.cardBorder}`,
    borderLeft: `3px solid ${colors.accent}`,
    borderRadius: 8,
    padding: "16px 18px",
    fontSize: 14,
    color: colors.text,
    lineHeight: 1.65,
    marginBottom: 24,
  },

  // Location card
  locationCard: {
    background: colors.card,
    border: `1px solid ${colors.cardBorder}`,
    borderLeft: `3px solid ${colors.accent}`,
    borderRadius: 10,
    padding: "18px 20px",
    marginBottom: 24,
  },
  locationCampus: {
    fontSize: 18,
    fontWeight: 800,
    color: colors.text,
    marginBottom: 6,
  },
  locationAddress: {
    fontSize: 14,
    color: colors.textMuted,
    fontWeight: 600,
    lineHeight: 1.5,
  },

  // Form
  formCard: {
    background: colors.card,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 14,
    padding: "24px 22px",
    marginBottom: 24,
  },
  formIntro: {
    fontSize: 14,
    color: colors.textMuted,
    lineHeight: 1.55,
    marginBottom: 20,
  },
  fieldGroup: {
    marginBottom: 18,
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: colors.textMuted,
    marginBottom: 6,
  },
  req: {
    color: colors.accent,
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    fontSize: 15,
    fontFamily: font,
    background: colors.input,
    border: `1px solid ${colors.inputBorder}`,
    borderRadius: 8,
    color: colors.text,
    outline: "none",
    boxSizing: "border-box",
  },
  hint: {
    fontSize: 12,
    color: colors.textMuted,
    marginTop: 6,
    fontStyle: "italic",
    lineHeight: 1.5,
  },

  // Subtotal
  subtotalBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 18px",
    background: colors.accentGlow,
    border: `1px solid ${colors.accent}33`,
    borderRadius: 10,
    marginBottom: 20,
  },
  subtotalLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: colors.text,
  },
  subtotalValue: {
    fontSize: 22,
    fontWeight: 800,
    color: colors.accent,
  },

  // Buttons
  primaryBtn: {
    width: "100%",
    padding: "14px 24px",
    fontSize: 16,
    fontWeight: 700,
    fontFamily: font,
    background: `linear-gradient(135deg, ${colors.accent}, #1a4d9e)`,
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    transition: "opacity 0.2s",
  },
  btnDisabled: {
    opacity: 0.35,
    cursor: "not-allowed",
  },
  stripeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: "14px 24px",
    fontSize: 16,
    fontWeight: 700,
    fontFamily: font,
    background: colors.stripe,
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    textDecoration: "none",
    marginTop: 14,
  },

  // Success
  successBox: {
    background: colors.card,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 14,
    padding: "32px 24px",
    textAlign: "center",
    marginBottom: 24,
  },
  successIcon: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: colors.success,
    color: "#fff",
    fontSize: 28,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 14px",
  },
  successTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: colors.success,
    marginBottom: 10,
  },
  successText: {
    fontSize: 14,
    color: colors.text,
    lineHeight: 1.6,
    marginBottom: 4,
  },

  // Footer
  footer: {
    textAlign: "center",
    fontSize: 12,
    color: colors.textMuted,
    padding: "24px 0",
    letterSpacing: 0.5,
  },
};
